/**
 * Consistent error handling for the portal API client (Doc §5.2 step 4). The CVSI backend returns
 * `{ error: { code, message } }` with conventional HTTP statuses; this normalizes any failure into a
 * single typed shape the UI can branch on (auth → redirect to login, forbidden → "no access" state,
 * rate-limited → backoff toast, etc.).
 */

export type CvsiErrorKind =
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'invalid_argument'
  | 'rate_limited'
  | 'conflict'
  | 'server'
  | 'network'
  | 'unknown';

export class CvsiError extends Error {
  constructor(
    public readonly kind: CvsiErrorKind,
    public readonly status: number,
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'CvsiError';
  }
}

function kindForStatus(status: number): CvsiErrorKind {
  switch (status) {
    case 401: return 'unauthenticated';
    case 403: return 'forbidden';
    case 404: return 'not_found';
    case 400: return 'invalid_argument';
    case 409: return 'conflict';
    case 429: return 'rate_limited';
    default: return status >= 500 ? 'server' : 'unknown';
  }
}

/** Turn a non-OK `fetch` Response into a `CvsiError` (reads the JSON error envelope when present). */
export async function normalizeCvsiError(res: Response): Promise<CvsiError> {
  let message = res.statusText || 'Request failed';
  let code: string | undefined;
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string }; message?: string };
    message = body?.error?.message || body?.message || message;
    code = body?.error?.code;
  } catch {
    // non-JSON body; keep the status text
  }
  return new CvsiError(kindForStatus(res.status), res.status, message, code);
}
